import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import {
  Newspaper, ArrowLeft, Search, ExternalLink, Clock, RefreshCw,
  Bookmark, Share2, Filter, TrendingUp, Calendar, Globe,
  Loader2, AlertCircle, ChevronRight, Zap
} from "lucide-react";

/**
 * TGPSC News Hub - Current Affairs for Telangana Group-1
 * Reuses UPSC News Hub structure with Telangana-specific filters
 */

const TGPSCNewsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [error, setError] = useState(null);

  const isPro = user?.role === "PRO";

  // TGPSC-relevant categories
  const categories = [
    { id: "all", label: "All News", icon: Globe },
    { id: "telangana", label: "Telangana", icon: TrendingUp },
    { id: "national", label: "National", icon: Globe },
    { id: "economy", label: "Economy", icon: TrendingUp },
    { id: "polity", label: "Polity", icon: Globe },
    { id: "environment", label: "Environment", icon: Globe },
  ];

  useEffect(() => {
    fetchNews();
  }, [selectedCategory]);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(
        `${API_BASE}/news/feed?category=${selectedCategory}&exam_type=TGPSC`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNews(response.data.articles || []);
    } catch (err) {
      console.error("Error fetching news:", err);
      setError("Failed to load news. Please try again.");
      // Show sample news for demo
      setNews([
        {
          id: 1,
          title: "Telangana Budget 2026-27: Key Highlights for TGPSC Aspirants",
          summary: "The Telangana state budget emphasizes welfare schemes, IT sector growth, and agricultural development...",
          category: "telangana",
          source: "The Hindu",
          date: new Date().toISOString(),
          relevance: "high"
        },
        {
          id: 2,
          title: "Kaleshwaram Project: Latest Updates and Analysis",
          summary: "Understanding the world's largest lift irrigation project and its impact on Telangana's agriculture...",
          category: "telangana",
          source: "Indian Express",
          date: new Date().toISOString(),
          relevance: "high"
        },
        {
          id: 3,
          title: "RBI Monetary Policy: Impact on State Economies",
          summary: "Analysis of the latest RBI policy decisions and their implications for Telangana's economy...",
          category: "economy",
          source: "Economic Times",
          date: new Date().toISOString(),
          relevance: "medium"
        },
        {
          id: 4,
          title: "Constitutional Amendment: 106th Amendment Explained",
          summary: "Understanding the recent constitutional amendment and its relevance for TGPSC Mains...",
          category: "polity",
          source: "The Hindu",
          date: new Date().toISOString(),
          relevance: "high"
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = news.filter(article => 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRelevanceBadge = (relevance) => {
    switch (relevance) {
      case "high": return { color: "bg-red-500", text: "High Relevance" };
      case "medium": return { color: "bg-amber-500", text: "Medium" };
      default: return { color: "bg-slate-500", text: "General" };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30 pb-20">
      {/* Back Navigation */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/tgpsc")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          TGPSC Dashboard
        </Button>
      </div>

      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-6 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
            <Newspaper className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              TGPSC News Hub
            </h1>
            <p className="text-slate-600">Current Affairs for Telangana Group-1</p>
          </div>
          <Badge className="bg-pink-500 ml-auto">TGPSC</Badge>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search news articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="max-w-5xl mx-auto px-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id 
                ? "bg-gradient-to-r from-orange-500 to-amber-600 text-white" 
                : ""
              }
            >
              <cat.icon className="w-4 h-4 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>
      </div>

      {/* News Feed */}
      <div className="max-w-5xl mx-auto px-4">
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-orange-500 animate-spin mb-4" />
              <p className="text-slate-600">Loading news feed...</p>
            </CardContent>
          </Card>
        ) : error && news.length === 0 ? (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <div>
                  <p className="font-medium text-red-800">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchNews}
                    className="mt-2"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNews.map((article) => {
              const relevance = getRelevanceBadge(article.relevance);
              return (
                <Card key={article.id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {article.category}
                          </Badge>
                          <Badge className={`${relevance.color} text-white text-xs`}>
                            {relevance.text}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-2 hover:text-orange-600 cursor-pointer">
                          {article.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {article.source}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(article.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Bookmark className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Link to Editorial Analyzer */}
      <div className="max-w-5xl mx-auto px-4 mt-8">
        <Card 
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-all"
          onClick={() => navigate("/tgpsc/editorial")}
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-8 h-8" />
                <div>
                  <h3 className="font-bold text-lg">Editorial Analyzer</h3>
                  <p className="text-cyan-100 text-sm">Analyze Hindu/Indian Express editorials for TGPSC</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TGPSCNewsHubPage;
